// Social panel live preview: fills a lesson template exactly as the Mac
// render script does (the shared module WAW-Content-Engine/shared/lessonFill.mjs),
// with the pose images as data: URIs, and adds a reporter that reads the
// template's own fit lines the way render-lesson-posts.mjs reads them: after
// the page loads, wait for the fonts and images, run the fit script once more
// (window.run), then read p.fit in every figure.pv.

import { fillTemplate } from '../../../shared/lessonFill.mjs';
import type { LessonPackage, LessonPlatform, LessonPost } from '../../../shared/lessonFill.mjs';
import { api } from '../services/api';

export type { LessonPackage, LessonPlatform, LessonPost };

const templateCache = new Map<string, Promise<string>>();
const poseCache = new Map<string, Promise<string>>();

export function loadTemplate(sha: string): Promise<string> {
  if (!templateCache.has(sha)) {
    const p = api.getBytes(`/api/social/panel/templates/${sha}`).then(({ bytes }) => new TextDecoder().decode(bytes));
    p.catch(() => templateCache.delete(sha));
    templateCache.set(sha, p);
  }
  return templateCache.get(sha)!;
}

function base64(bytes: ArrayBuffer): string {
  const u8 = new Uint8Array(bytes);
  let s = '';
  for (let i = 0; i < u8.length; i += 0x8000) s += String.fromCharCode(...u8.subarray(i, i + 0x8000));
  return btoa(s);
}

// Same src the Mac builds: 'data:image/png;base64,' + the file's bytes.
export function loadPose(code: string): Promise<string> {
  if (!poseCache.has(code)) {
    const p = api.getBytes(`/api/social/panel/poses/${encodeURIComponent(code)}`).then(({ bytes }) => 'data:image/png;base64,' + base64(bytes));
    p.catch(() => poseCache.delete(code));
    poseCache.set(code, p);
  }
  return poseCache.get(code)!;
}

function reporter(token: string): string {
  return '<script>window.addEventListener("load",function(){' +
    'Promise.resolve(document.fonts.ready).then(function(){' +
    'return Promise.all(Array.prototype.map.call(document.images,function(i){return i.decode().catch(function(){});}));' +
    '}).then(function(){window.run();' +
    'var fits=Array.prototype.map.call(document.querySelectorAll("figure.pv"),function(f){var p=f.querySelector("p.fit");return p?p.textContent:"";});' +
    `parent.postMessage({wawLessonFit:${JSON.stringify(token)},fits:fits},"*");});});</script>`;
}

/** The filled page for one platform, with the fit reporter added before </body>. Throws FillStop on a bad post. */
export function previewHtml(template: string, platform: LessonPlatform, post: LessonPost, poses: Record<string, string>, pkg: LessonPackage, token: string): string {
  const html = fillTemplate(template, platform.file, post, poses, pkg);
  const at = html.lastIndexOf('</body>');
  if (at < 0) throw new Error(`${platform.file} has no </body>`);
  return html.slice(0, at) + reporter(token) + html.slice(at);
}

// figure.pv { margin: 0 0 24px } in every lesson template: each slide takes its height plus 24 px.
export const SLIDE_GAP = 24;
